import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, CameraIcon, FileTextIcon, MapPinIcon, FingerprintIcon, UserIcon } from 'lucide-react';
import { TopBar } from '../components/TopBar';
interface KYCProps {
  navigate: (screen: string) => void;
}
export function KYC({
  navigate
}: KYCProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0); // 0: BVN, 1: NIN, 2: Selfie, 3: Address, 4: Done
  const [isVerifying, setIsVerifying] = useState(false);
  const [bvn, setBvn] = useState('');
  const [nin, setNin] = useState('');
  const [addressForm, setAddressForm] = useState({
    provider: '',
    meterNumber: '',
    address: ''
  });
  const handleNextStep = () => {
    setIsVerifying(true);
    setTimeout(() => {
      setIsVerifying(false);
      if (currentStepIndex < 4) {
        setCurrentStepIndex(prev => prev + 1);
      }
    }, 1500);
  };
  const handleFinish = () => {
    sessionStorage.setItem('kyc_status', 'completed');
    navigate('dashboard');
  };
  const steps = [{
    id: 1,
    title: 'BVN',
    icon: FingerprintIcon,
    status: currentStepIndex > 0 ? 'completed' : 'current',
    desc: 'Verified on Mar 10, 2026'
  }, {
    id: 2,
    title: 'NIN',
    icon: UserIcon,
    status: currentStepIndex > 1 ? 'completed' : currentStepIndex === 1 ? 'current' : 'pending',
    desc: 'National Identity Number'
  }, {
    id: 3,
    title: 'Selfie',
    icon: CameraIcon,
    status: currentStepIndex > 2 ? 'completed' : currentStepIndex === 2 ? 'current' : 'pending',
    desc: 'Verify your identity matches your ID'
  }, {
    id: 4,
    title: 'Address',
    icon: MapPinIcon,
    status: currentStepIndex > 3 ? 'completed' : currentStepIndex === 3 ? 'current' : 'pending',
    desc: 'Verify via electricity meter'
  }];
  // Calculate progress bar width based on completed steps
  const progressWidth = `${currentStepIndex / (steps.length - 1) * 100}%`;
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="flex-1 overflow-y-auto pb-safe bg-slate-50" data-id="element-2138">
      <TopBar title="KYC Verification" showBack onBack={() => navigate('profile')} data-id="element-2139" />

      <div className="px-5 pt-6 pb-8" data-id="element-2140">
        {/* Progress Indicator */}
        <div className="flex items-center justify-between mb-10 relative px-2" data-id="element-2141">
          <div className="absolute left-6 right-6 top-1/2 -translate-y-1/2 h-1 bg-gray-200 -z-10 rounded-full" data-id="element-2142"></div>
          <motion.div className="absolute left-6 top-1/2 -translate-y-1/2 h-1 bg-emerald-500 -z-10 rounded-full" animate={{
          width: progressWidth
        }} transition={{
          duration: 0.5
        }} data-id="element-2143" />

          {steps.map(step => <div key={step.id} className="flex flex-col items-center gap-2 bg-slate-50 px-1" data-id="element-2144">
              <motion.div animate={{
            backgroundColor: step.status === 'completed' ? '#10b981' : step.status === 'current' ? '#ffffff' : '#ffffff',
            borderColor: step.status === 'completed' ? '#10b981' : step.status === 'current' ? '#f59e0b' : '#d1d5db',
            color: step.status === 'completed' ? '#ffffff' : step.status === 'current' ? '#f59e0b' : '#d1d5db',
            boxShadow: step.status === 'current' ? '0 0 0 4px rgba(245,158,11,0.2)' : 'none'
          }} className="w-8 h-8 rounded-full flex items-center justify-center border-2" data-id="element-2145">
                {step.status === 'completed' ? <CheckCircleIcon className="w-5 h-5" data-id="element-2146" /> : <step.icon className="w-4 h-4" data-id="element-2147" />}
              </motion.div>
              <span className={`text-[10px] font-bold uppercase tracking-wider ${step.status === 'completed' ? 'text-emerald-600' : step.status === 'current' ? 'text-amber-600' : 'text-gray-400'}`} data-id="element-2148">
                {step.title}
              </span>
            </div>)}
        </div>

        {/* Current Step Card */}
        <AnimatePresence mode="wait" data-id="element-2149">
          {currentStepIndex === 0 && <motion.div key="bvn" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="bg-white rounded-3xl p-6 shadow-lg shadow-gray-200/50 border border-gray-100 mb-8 text-center" data-id="element-2150">
              <div className="w-20 h-20 rounded-full bg-amber-50 flex items-center justify-center text-amber-500 mx-auto mb-4 border-4 border-amber-100" data-id="element-2151">
                <FingerprintIcon className="w-10 h-10" data-id="element-2152" />
              </div>

              <h2 className="text-xl font-bold text-[#0D3B3B] mb-2" data-id="element-2153">
                Verify BVN
              </h2>
              <p className="text-sm text-gray-500 leading-relaxed mb-6" data-id="element-2154">
                Enter your 11-digit Bank Verification Number to verify your
                identity securely.
              </p>

              <div className="mb-6 text-left" data-id="element-2155">
                <label className="text-sm font-bold text-[#0D3B3B] ml-1 mb-2 block" data-id="element-2156">
                  BVN Number
                </label>
                <input type="text" maxLength={11} value={bvn} onChange={e => setBvn(e.target.value.replace(/\D/g, ''))} placeholder="Enter 11-digit BVN" className="w-full bg-gray-50 border border-gray-200 rounded-xl py-4 px-4 text-base font-medium focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all text-center tracking-widest" data-id="element-2157" />
              </div>

              <button onClick={handleNextStep} disabled={isVerifying || bvn.length !== 11} className={`w-full font-bold py-4 rounded-xl transition-all shadow-lg active:scale-95 text-lg flex items-center justify-center gap-2 ${isVerifying || bvn.length !== 11 ? 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none' : 'bg-amber-500 hover:bg-amber-400 text-[#0D3B3B] shadow-amber-500/30'}`} data-id="element-2158">
                {isVerifying ? <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" data-id="element-2159" /> : 'Verify BVN'}
              </button>
            </motion.div>}

          {currentStepIndex === 1 && <motion.div key="nin" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          y: 0,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="bg-white rounded-3xl p-6 shadow-lg shadow-gray-200/50 border border-gray-100 mb-8 text-center" data-id="element-2160">
              <div className="w-20 h-20 rounded-full bg-amber-50 flex items-center justify-center text-amber-500 mx-auto mb-4 border-4 border-amber-100" data-id="element-2161">
                <UserIcon className="w-10 h-10" data-id="element-2162" />
              </div>

              <h2 className="text-xl font-bold text-[#0D3B3B] mb-2" data-id="element-2163">
                Verify NIN
              </h2>
              <p className="text-sm text-gray-500 leading-relaxed mb-6" data-id="element-2164">
                Enter your 11-digit National Identity Number to continue
                verification.
              </p>

              <div className="mb-6 text-left" data-id="element-2165">
                <label className="text-sm font-bold text-[#0D3B3B] ml-1 mb-2 block" data-id="element-2166">
                  NIN Number
                </label>
                <input type="text" maxLength={11} value={nin} onChange={e => setNin(e.target.value.replace(/\D/g, ''))} placeholder="Enter 11-digit NIN" className="w-full bg-gray-50 border border-gray-200 rounded-xl py-4 px-4 text-base font-medium focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all text-center tracking-widest" data-id="element-2167" />
              </div>

              <button onClick={handleNextStep} disabled={isVerifying || nin.length !== 11} className={`w-full font-bold py-4 rounded-xl transition-all shadow-lg active:scale-95 text-lg flex items-center justify-center gap-2 ${isVerifying || nin.length !== 11 ? 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none' : 'bg-amber-500 hover:bg-amber-400 text-[#0D3B3B] shadow-amber-500/30'}`} data-id="element-2168">
                {isVerifying ? <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" data-id="element-2169" /> : 'Verify NIN'}
              </button>
            </motion.div>}

          {currentStepIndex === 2 && <motion.div key="selfie" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="bg-white rounded-3xl p-6 shadow-lg shadow-gray-200/50 border border-gray-100 mb-8 text-center" data-id="element-2170">
              <div className="w-20 h-20 rounded-full bg-amber-50 flex items-center justify-center text-amber-500 mx-auto mb-4 border-4 border-amber-100" data-id="element-2171">
                <CameraIcon className="w-10 h-10" data-id="element-2172" />
              </div>

              <h2 className="text-xl font-bold text-[#0D3B3B] mb-2" data-id="element-2173">
                Take a Selfie
              </h2>
              <p className="text-sm text-gray-500 leading-relaxed mb-6" data-id="element-2174">
                We need to verify your identity matches your ID document. Please
                take a clear photo of your face.
              </p>

              <div className="bg-gray-50 rounded-2xl p-4 text-left mb-6 border border-gray-100" data-id="element-2175">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2176">
                  Requirements
                </h4>
                <ul className="space-y-2" data-id="element-2177">
                  <li className="flex items-center gap-2 text-sm font-medium text-[#0D3B3B]" data-id="element-2178">
                    <CheckCircleIcon className="w-4 h-4 text-emerald-500" data-id="element-2179" />{' '}
                    Good lighting
                  </li>
                  <li className="flex items-center gap-2 text-sm font-medium text-[#0D3B3B]" data-id="element-2180">
                    <CheckCircleIcon className="w-4 h-4 text-emerald-500" data-id="element-2181" /> No
                    glasses or hats
                  </li>
                  <li className="flex items-center gap-2 text-sm font-medium text-[#0D3B3B]" data-id="element-2182">
                    <CheckCircleIcon className="w-4 h-4 text-emerald-500" data-id="element-2183" />{' '}
                    Face the camera directly
                  </li>
                </ul>
              </div>

              <button onClick={handleNextStep} disabled={isVerifying} className={`w-full font-bold py-4 rounded-xl transition-all shadow-lg active:scale-95 text-lg flex items-center justify-center gap-2 ${isVerifying ? 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none' : 'bg-amber-500 hover:bg-amber-400 text-[#0D3B3B] shadow-amber-500/30'}`} data-id="element-2184">
                {isVerifying ? <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" data-id="element-2185" /> : 'Take Selfie'}
              </button>
            </motion.div>}

          {currentStepIndex === 3 && <motion.div key="address" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          y: 0,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="bg-white rounded-3xl p-6 shadow-lg shadow-gray-200/50 border border-gray-100 mb-8 text-center" data-id="element-2186">
              <div className="w-20 h-20 rounded-full bg-amber-50 flex items-center justify-center text-amber-500 mx-auto mb-4 border-4 border-amber-100" data-id="element-2187">
                <MapPinIcon className="w-10 h-10" data-id="element-2188" />
              </div>

              <h2 className="text-xl font-bold text-[#0D3B3B] mb-2" data-id="element-2189">
                Verify Address
              </h2>
              <p className="text-sm text-gray-500 leading-relaxed mb-6" data-id="element-2190">
                Provide your electricity meter details to verify your
                residential address.
              </p>

              <div className="space-y-4 mb-6 text-left" data-id="element-2191">
                <div data-id="element-2192">
                  <label className="text-sm font-bold text-[#0D3B3B] ml-1 mb-2 block" data-id="element-2193">
                    Electricity Provider
                  </label>
                  <select value={addressForm.provider} onChange={e => setAddressForm({
                ...addressForm,
                provider: e.target.value
              })} className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3 px-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all text-[#0D3B3B]" data-id="element-2194">
                    <option value="" disabled data-id="element-2195">
                      Select Provider
                    </option>
                    <option value="ikeja" data-id="element-2196">Ikeja Electric (IE)</option>
                    <option value="eko" data-id="element-2197">Eko Electricity (EKEDC)</option>
                    <option value="abuja" data-id="element-2198">Abuja Electricity (AEDC)</option>
                    <option value="ibadan" data-id="element-2199">Ibadan Electricity (IBEDC)</option>
                    <option value="kano" data-id="element-2200">Kano Electricity (KEDCO)</option>
                  </select>
                </div>
                <div data-id="element-2201">
                  <label className="text-sm font-bold text-[#0D3B3B] ml-1 mb-2 block" data-id="element-2202">
                    Meter Number
                  </label>
                  <input type="text" value={addressForm.meterNumber} onChange={e => setAddressForm({
                ...addressForm,
                meterNumber: e.target.value
              })} placeholder="Enter meter number" className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3 px-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all text-[#0D3B3B]" data-id="element-2203" />
                </div>
                <div data-id="element-2204">
                  <label className="text-sm font-bold text-[#0D3B3B] ml-1 mb-2 block" data-id="element-2205">
                    Residential Address
                  </label>
                  <textarea value={addressForm.address} onChange={e => setAddressForm({
                ...addressForm,
                address: e.target.value
              })} placeholder="Enter your full residential address" rows={3} className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3 px-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all text-[#0D3B3B] resize-none" data-id="element-2206" />
                </div>
              </div>

              <button onClick={handleNextStep} disabled={isVerifying || !addressForm.provider || !addressForm.meterNumber || !addressForm.address} className={`w-full font-bold py-4 rounded-xl transition-all shadow-lg active:scale-95 text-lg flex items-center justify-center gap-2 ${isVerifying || !addressForm.provider || !addressForm.meterNumber || !addressForm.address ? 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none' : 'bg-amber-500 hover:bg-amber-400 text-[#0D3B3B] shadow-amber-500/30'}`} data-id="element-2207">
                {isVerifying ? <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" data-id="element-2208" /> : 'Verify Address'}
              </button>
            </motion.div>}

          {currentStepIndex === 4 && <motion.div key="complete" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="bg-white rounded-3xl p-8 shadow-lg shadow-gray-200/50 border border-gray-100 mb-8 text-center" data-id="element-2209">
              <div className="w-24 h-24 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-500 mx-auto mb-6" data-id="element-2210">
                <CheckCircleIcon className="w-12 h-12" data-id="element-2211" />
              </div>

              <h2 className="text-2xl font-black text-[#0D3B3B] mb-2" data-id="element-2212">
                Verification Complete!
              </h2>
              <p className="text-sm text-gray-500 leading-relaxed mb-8" data-id="element-2213">
                Your identity and address have been successfully verified. You
                now have full access to all Skydd features.
              </p>

              <button onClick={handleFinish} className="w-full bg-[#0D3B3B] hover:bg-[#092D2D] text-[#FFD700] font-bold py-4 rounded-xl transition-all shadow-lg shadow-[#0D3B3B]/20 active:scale-95 text-lg" data-id="element-2214">
                Return to Dashboard
              </button>
            </motion.div>}
        </AnimatePresence>

        {/* Completed & Pending Steps List */}
        {currentStepIndex < 4 && <div className="space-y-3" data-id="element-2215">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 px-1" data-id="element-2216">
              Verification Status
            </h3>

            {steps.filter(s => s.status === 'completed').map(step => <div key={step.id} className="bg-white rounded-2xl p-4 border border-emerald-100 shadow-sm flex items-center gap-4" data-id="element-2217">
                  <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-500 shrink-0" data-id="element-2218">
                    <CheckCircleIcon className="w-5 h-5" data-id="element-2219" />
                  </div>
                  <div data-id="element-2220">
                    <h4 className="font-bold text-[#0D3B3B] text-sm" data-id="element-2221">
                      {step.title} Verification
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-2222">{step.desc}</p>
                  </div>
                </div>)}

            {steps.filter(s => s.status === 'pending').map(step => <div key={step.id} className="bg-gray-50 rounded-2xl p-4 border border-gray-200 border-dashed flex items-center gap-4 opacity-70" data-id="element-2223">
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 shrink-0" data-id="element-2224">
                    <step.icon className="w-5 h-5" data-id="element-2225" />
                  </div>
                  <div data-id="element-2226">
                    <h4 className="font-bold text-gray-600 text-sm" data-id="element-2227">
                      {step.title}
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-2228">{step.desc}</p>
                  </div>
                </div>)}
          </div>}
      </div>
    </motion.div>;
}